import type { NamespaceId } from "@/types/namespaces";
import type { WorkspacePreferenceScope } from "@/types/workspace-preferences";

export const queryKeys = {
  namespaces: () => ["namespaces"] as const,
  namespaceDetail: (namespaceId: NamespaceId) => ["namespaceDetail", namespaceId] as const,
  namespaceProvisioning: (namespaceId: NamespaceId) =>
    ["namespaceProvisioning", namespaceId] as const,
  namespaceTasks: (
    namespaceId: NamespaceId,
    params?: {
      state?: string | null;
      search?: string | null;
      sort?: string | null;
      createdFrom?: string | null;
      createdTo?: string | null;
      page?: number | null;
      pageSize?: number | null;
    },
  ) =>
    [
      "namespaceTasks",
      namespaceId,
      params?.state ?? "active",
      params?.search ?? "",
      params?.sort ?? "",
      params?.createdFrom ?? "",
      params?.createdTo ?? "",
      params?.page ?? 1,
      params?.pageSize ?? 0,
    ] as const,
  namespaceTask: (namespaceId: NamespaceId, taskNumber: number) =>
    ["namespaceTask", namespaceId, taskNumber] as const,
  goals: (namespaceId: NamespaceId) => ["goals", namespaceId] as const,
  goalContext: (namespaceId: NamespaceId, goalId: string) =>
    ["goalContext", namespaceId, goalId] as const,
  goalTasks: (namespaceId: NamespaceId, goalId: string) =>
    ["goalTasks", namespaceId, goalId] as const,
  goalMessages: (namespaceId: NamespaceId, goalId: string) =>
    ["goalMessages", namespaceId, goalId] as const,
  goalRun: (namespaceId: NamespaceId, goalId: string) =>
    ["goalRun", namespaceId, goalId] as const,
  goalActivity: (namespaceId: NamespaceId, goalId: string) =>
    ["goalActivity", namespaceId, goalId] as const,
  cronJobs: (namespaceId: NamespaceId) => ["cronJobs", namespaceId] as const,
  cronJobKinds: (namespaceId: NamespaceId) => ["cronJobKinds", namespaceId] as const,
  cronJobRuns: (
    namespaceId: NamespaceId,
    params?: { jobId?: string | null; result?: string | null; cursor?: string | null; limit?: number | null },
  ) =>
    [
      "cronJobRuns",
      namespaceId,
      params?.jobId ?? null,
      params?.result ?? null,
      params?.cursor ?? null,
      params?.limit ?? null,
    ] as const,
  namespaceRepositories: (namespaceId: NamespaceId) => ["namespaceRepositories", namespaceId] as const,
  namespaceDocumentsTree: (namespaceId: NamespaceId) =>
    ["namespaceDocumentsTree", namespaceId] as const,
  namespaceDocument: (namespaceId: NamespaceId, documentPath: string) =>
    ["namespaceDocument", namespaceId, documentPath] as const,
  repositoryTree: (
    namespaceId: NamespaceId,
    repositoryId: string,
    params?: {
      path?: string;
      branch?: string;
      commit?: string;
      filter?: string;
      pageSize?: number;
      pageToken?: string;
    },
  ) =>
    [
      "repositoryTree",
      namespaceId,
      repositoryId,
      params?.path ?? "",
      params?.branch ?? null,
      params?.commit ?? null,
      params?.filter ?? null,
      params?.pageSize ?? null,
      params?.pageToken ?? null,
    ] as const,
  repositoryFile: (
    namespaceId: NamespaceId,
    repositoryId: string,
    path: string,
    params?: { branch?: string; commit?: string },
  ) => [
    "repositoryFile",
    namespaceId,
    repositoryId,
    path,
    params?.branch ?? null,
    params?.commit ?? null,
  ] as const,
  repositoryBranches: (namespaceId: NamespaceId, repositoryId: string) => [
    "repositoryBranches",
    namespaceId,
    repositoryId,
  ] as const,
  repositoryBranchSource: (
    namespaceId: NamespaceId,
    repositoryId: string,
    head: string,
  ) => ["repositoryBranchSource", namespaceId, repositoryId, head] as const,
  repositoryCommits: (
    namespaceId: NamespaceId,
    repositoryId: string,
    params?: { branch?: string; pageToken?: string },
  ) => [
    "repositoryCommits",
    namespaceId,
    repositoryId,
    params?.branch ?? null,
    params?.pageToken ?? null,
  ] as const,
  repositoryDiff: (
    namespaceId: NamespaceId,
    repositoryId: string,
    base: string,
    head: string,
  ) => ["repositoryDiff", namespaceId, repositoryId, base, head] as const,
  repositoryFileDiff: (
    namespaceId: NamespaceId,
    repositoryId: string,
    base: string,
    head: string,
    path: string,
  ) => ["repositoryFileDiff", namespaceId, repositoryId, base, head, path] as const,
  workspacePreferences: () => ["workspacePreferences"] as const,
  workspacePreference: (scope: WorkspacePreferenceScope) => ["workspacePreference", scope] as const,
  slackInstallation: (orgId: string) => ["slackInstallation", orgId] as const,
  adminTokenUsageBreakdown: (from?: string, to?: string, lastMinutes?: number) =>
    ["adminTokenUsageBreakdown", from, to, lastMinutes] as const,
  adminOrganizations: () => ["adminOrganizations"] as const,
  adminOrganization: (orgId: string) => ["adminOrganization", orgId] as const,
  adminNamespaces: () => ["adminNamespaces"] as const,
  adminNamespace: (namespaceId: string) => ["adminNamespace", namespaceId] as const,
  adminFailedAudits: () => ["adminFailedAudits"] as const,
  adminUsers: (orgId: string) => ["adminUsers", orgId] as const,
  adminUsersUnassigned: () => ["adminUsers", "unassigned"] as const,
  adminUser: (userId: string) => ["adminUser", userId] as const,
  adminInvites: (orgId: string) => ["adminInvites", orgId] as const,
  adminOrgFeatures: (orgId: string) => ["adminOrgFeatures", orgId] as const,
  namespaceFeatures: (namespaceId: string) => ["namespaceFeatures", namespaceId] as const,
  improvementSettings: (namespaceId: string) => ["improvementSettings", namespaceId] as const,
  improvementPatterns: (namespaceId: string) => ["improvementPatterns", namespaceId] as const,
  adminSlackBindings: (namespaceId: string) => ["adminSlackBindings", namespaceId] as const,
  adminLlmProviders: () => ["adminLlmProviders"] as const,
  adminLlmInFlight: () => ["adminLlmInFlight"] as const,
  adminLlmProviderGroups: () => ["adminLlmProviderGroups"] as const,
  adminLlmProviderGroup: (id: string) => ["adminLlmProviderGroup", id] as const,
  orgLlmProviders: () => ["orgLlmProviders"] as const,
  orgLlmInFlight: () => ["orgLlmInFlight"] as const,
  orgLlmProviderGroups: () => ["orgLlmProviderGroups"] as const,
  orgTailnets: () => ["orgTailnets"] as const,
  orgTailnetEndpointMetrics: () => ["orgTailnetEndpointMetrics"] as const,
  orgTailnetPools: () => ["orgTailnetPools"] as const,
  myOrganization: () => ["myOrganization"] as const,
  adminOrgTailnets: () => ["adminOrgTailnets"] as const,
  adminLlmProvider: (id: string) => ["adminLlmProvider", id] as const,
  adminHfEndpoints: () => ["adminHfEndpoints"] as const,
  adminLlmBackends: () => ["adminLlmBackends"] as const,
  adminLlmBackend: (key: string) => ["adminLlmBackend", key] as const,
  adminLlmBackendPresets: (key: string) => ["adminLlmBackendPresets", key] as const,
  orgLlmBackends: () => ["orgLlmBackends"] as const,
  adminDocumentationClasses: (orgId: string) => ["adminDocumentationClasses", orgId] as const,
  orgDocumentationClasses: () => ["orgDocumentationClasses"] as const,
  adminDocumentationIntegrations: () => ["adminDocumentationIntegrations"] as const,
  adminDocumentationIntegration: (id: string) => ["adminDocumentationIntegration", id] as const,
  adminDocumentationConnectorKinds: () => ["adminDocumentationConnectorKinds"] as const,
  adminDocumentationWriteLog: (id: string) => ["adminDocumentationWriteLog", id] as const,
  namespaceAgentSkills: (namespaceId: string) =>
    ["namespaceAgentSkills", namespaceId] as const,
  namespaceNotificationChannels: (namespaceId: string) =>
    ["namespaceNotificationChannels", namespaceId] as const,
  namespaceDocumentationIntegrations: (namespaceId: string) =>
    ["namespaceDocumentationIntegrations", namespaceId] as const,
  namespaceDocumentationConnectorKinds: (namespaceId: string) =>
    ["namespaceDocumentationConnectorKinds", namespaceId] as const,
  adminAgentSource: () => ["adminAgentSource"] as const,
  adminSystemNotifications: () => ["adminSystemNotifications"] as const,
  adminAgentClusterNamespaces: () => ["adminAgentClusterNamespaces"] as const,
  adminAgentClusterNamespace: (id: string) => ["adminAgentClusterNamespace", id] as const,
  adminInternalServiceCatalog: () => ["adminInternalServiceCatalog"] as const,
  adminAgentNamespaceAudit: (id: string) => ["adminAgentNamespaceAudit", id] as const,
  agentInstances: () => ["agentInstances"] as const,
  agentGroups: () => ["agentGroups"] as const,
  poolWorkloads: () => ["poolWorkloads"] as const,
  agentDefinitions: () => ["agentDefinitions"] as const,
  agentDefinition: (slug: string) => ["agentDefinition", slug] as const,
  toolCategories: () => ["admin", "tool-categories"] as const,
  agentGroupNamespaces: (groupId: string) => ["agentGroupNamespaces", groupId] as const,
  agentLog: (slug: string) => ["agentLog", slug] as const,
  auditRuns: (namespaceId: NamespaceId) => ["auditRuns", namespaceId] as const,
  auditFindings: (
    namespaceId: NamespaceId,
    runId: string,
    params?: { type?: string | null; status?: string | null; page?: number | null },
  ) =>
    [
      "auditFindings",
      namespaceId,
      runId,
      params?.type ?? null,
      params?.status ?? null,
      params?.page ?? null,
    ] as const,
  auditNamespaceFindings: (
    namespaceId: NamespaceId,
    params?: { type?: string | null; page?: number | null; pageSize?: number | null },
  ) =>
    [
      "auditNamespaceFindings",
      namespaceId,
      params?.type ?? null,
      params?.page ?? null,
      params?.pageSize ?? null,
    ] as const,
  auditSettings: (namespaceId: NamespaceId) => ["auditSettings", namespaceId] as const,
  prCheckSettings: (namespaceId: NamespaceId) => ["prCheckSettings", namespaceId] as const,
  conventions: (namespaceId: NamespaceId, status?: string | null) =>
    ["conventions", namespaceId, status ?? null] as const,
  conventionViolations: (namespaceId: NamespaceId) =>
    ["conventions", namespaceId, "violations"] as const,
  libraries: (
    namespaceId: NamespaceId,
    params?: {
      direct?: boolean | null;
      ecosystem?: string | null;
      name?: string | null;
      vulnerableOnly?: boolean;
      outdatedOnly?: boolean;
    },
  ) =>
    [
      "libraries",
      namespaceId,
      params?.direct ?? null,
      params?.ecosystem ?? null,
      params?.name ?? null,
      params?.vulnerableOnly ?? false,
      params?.outdatedOnly ?? false,
    ] as const,
  libraryAdvisories: (namespaceId: NamespaceId, libraryId?: string | null) =>
    ["libraryAdvisories", namespaceId, libraryId ?? null] as const,
  conventionDiscoveryStatus: (namespaceId: NamespaceId) =>
    ["conventionDiscoveryStatus", namespaceId] as const,
  auditStaleness: (namespaceId: NamespaceId) => ["audit", "staleness", namespaceId] as const,
  analysisStatus: (namespaceId: NamespaceId) => ["analysisStatus", namespaceId] as const,
  analysisDependencies: (
    namespaceId: NamespaceId,
    request: { queryType: string; target: string; depth?: number },
  ) => ["analysisDependencies", namespaceId, request.queryType, request.target, request.depth ?? 1] as const,
  analysisDocumentLinks: (namespaceId: NamespaceId, filePath: string) =>
    ["analysisDocumentLinks", namespaceId, filePath] as const,
  // Changes workspace (plan §3.1). The raw listing and the density buckets are
  // separate resources: the window decides which one is live, and their keys
  // must never alias each other.
  analysisCommits: (
    namespaceId: NamespaceId,
    params?: {
      repositoryId?: string | null;
      author?: string | null;
      since?: string | null;
      until?: string | null;
      path?: string | null;
      page?: number | null;
      pageSize?: number | null;
    },
  ) =>
    [
      "analysisCommits",
      namespaceId,
      params?.repositoryId ?? null,
      params?.author ?? null,
      params?.since ?? null,
      params?.until ?? null,
      params?.path ?? null,
      params?.page ?? null,
      params?.pageSize ?? null,
    ] as const,
  analysisCommitActivity: (
    namespaceId: NamespaceId,
    params?: {
      since?: string | null;
      until?: string | null;
      bucket?: string | null;
      repositoryId?: string | null;
    },
  ) =>
    [
      "analysisCommitActivity",
      namespaceId,
      params?.since ?? null,
      params?.until ?? null,
      params?.bucket ?? null,
      params?.repositoryId ?? null,
    ] as const,
  analysisCommitDetail: (namespaceId: NamespaceId, sha?: string | null) =>
    ["analysisCommitDetail", namespaceId, sha ?? null] as const,
  // Changelog generation (plan §3.4). The list and the single run are separate
  // resources: the list is a cheap history read, the single run is what polls
  // while a paid run is in flight.
  changelogRuns: (namespaceId: NamespaceId, limit?: number | null) =>
    ["changelogRuns", namespaceId, limit ?? null] as const,
  changelogRun: (namespaceId: NamespaceId, runId?: string | null) =>
    ["changelogRun", namespaceId, runId ?? null] as const,
  // Change impact (plan §3.2 / §3.3). The blast radius is keyed by commit alone:
  // BOTH sheet actions read the same entry, which is what makes "press the
  // second button, no second request" true rather than aspirational.
  analysisBlastRadius: (namespaceId: NamespaceId, sha?: string | null, crossings?: boolean) =>
    ["analysisBlastRadius", namespaceId, sha ?? null, crossings ?? false] as const,
  analysisImpactFromItem: (
    namespaceId: NamespaceId,
    itemRef?: string | null,
    params?: { since?: string | null; until?: string | null; blast?: boolean },
  ) =>
    [
      "analysisImpactFromItem",
      namespaceId,
      itemRef ?? null,
      params?.since ?? null,
      params?.until ?? null,
      params?.blast ?? false,
    ] as const,
  analysisQaRetest: (
    namespaceId: NamespaceId,
    params?: {
      since?: string | null;
      until?: string | null;
      repositoryId?: string | null;
    },
  ) =>
    [
      "analysisQaRetest",
      namespaceId,
      params?.since ?? null,
      params?.until ?? null,
      params?.repositoryId ?? null,
    ] as const,
  adminLlmAssignments: () => ["adminLlmAssignments"] as const,
  adminLlmAssignmentOverrides: () => ["adminLlmAssignmentOverrides"] as const,
  namespaceLlmAssignments: (id: string) => ["namespaceLlmAssignments", id] as const,
  namespaceLlmProviders: (id: string) => ["namespaceLlmProviders", id] as const,
  namespaceLlmProviderGroups: (id: string) => ["namespaceLlmProviderGroups", id] as const,
  namespaceEmbedding: (id: string) => ["namespaceEmbedding", id] as const,
  namespaceTokenUsage: (id: string, from?: string, to?: string, lastMinutes?: number) =>
    ["namespaceTokenUsage", id, from, to, lastMinutes] as const,
  onboardingState: () => ["onboardingState"] as const,
  providerConnection: (provider: string) => ["providerConnection", provider] as const,
  providerRepositories: (provider: string) => ["providerRepositories", provider] as const,
  gitProviderSources: () => ["gitProviderSources"] as const,
  gitProviderInstances: () => ["gitProviderInstances"] as const,
  // Legacy aliases (kept for any callers that haven't migrated).
  gitHubConnection: () => ["providerConnection", "github"] as const,
  gitHubRepositories: () => ["providerRepositories", "github"] as const,
};
